import { Component } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';

import { type AppLanguageCode, type AppLanguageOption } from '../../core/models/app-language.model';
import {
  type ComparativeObjectResponse
} from '../../core/models/comparative-object.model';
import { ComparativeObjectService } from '../../core/services/comparative-object.service';
import { DownloadFeedbackService } from '../../core/services/download-feedback.service';
import { TranslationService } from '../../core/services/translation.service';
import { UiPreferencesService } from '../../core/services/ui-preferences.service';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { FooterComponent } from '../layout/footer/footer.component';
import { NavbarComponent } from '../layout/navbar/navbar.component';
import { DatePickerComponent } from '../../shared/components/date-picker/date-picker.component';
import { extractHttpErrorMessage } from '../../core/utils/http-error.util';

@Component({
  selector: 'app-comparative-object',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, NavbarComponent, FooterComponent, TranslatePipe, DatePickerComponent],
  templateUrl: './comparative-object.component.html',
  styleUrl: './comparative-object.component.css'
})
export class ComparativeObjectComponent {
  protected readonly languages: readonly AppLanguageOption[];
  protected readonly darkMode;
  protected readonly form;

  protected isSubmitting = false;
  protected isExporting = false;
  protected errorMessage = '';
  protected exportErrorMessage = '';
  protected editableText = '';
  protected response: ComparativeObjectResponse | null = null;

  constructor(
    protected readonly translationService: TranslationService,
    private readonly uiPreferences: UiPreferencesService,
    private readonly formBuilder: FormBuilder,
    private readonly comparativeObjectService: ComparativeObjectService,
    private readonly downloadFeedback: DownloadFeedbackService
  ) {
    this.languages = this.translationService.languageOptions;
    this.darkMode = this.uiPreferences.darkMode;
    this.form = this.formBuilder.nonNullable.group({
      id: ['', Validators.required],
      cadastralArea: ['', Validators.required],
      parcelNumber: ['', Validators.required],
      totalArea: ['', [Validators.required, Validators.min(1)]],
      realizedPrice: ['', [Validators.required, Validators.min(0)]],
      date: ['', Validators.required],
      proceedingsNumber: ['', Validators.required],
      zoningCategory: ['', Validators.required]
    });
  }

  protected toggleTheme(): void {
    this.uiPreferences.toggleDarkMode();
  }

  protected async setLanguage(language: AppLanguageCode): Promise<void> {
    await this.translationService.setLanguage(language);
  }

  protected submit(): void {
    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      return;
    }

    const { id, cadastralArea, parcelNumber, totalArea, realizedPrice, date, proceedingsNumber, zoningCategory } =
      this.form.getRawValue();

    this.isSubmitting = true;
    this.errorMessage = '';
    this.exportErrorMessage = '';
    this.response = null;

    this.comparativeObjectService.generate({
      id: id.trim(),
      cadastralArea: cadastralArea.trim(),
      parcelNumber: parcelNumber.trim(),
      totalArea: Number(totalArea),
      realizedPrice: Number(realizedPrice),
      date: this.toApiDate(date),
      proceedingsNumber: proceedingsNumber.trim(),
      zoningCategory: zoningCategory.trim()
    }).subscribe({
      next: (response) => {
        this.response = response;
        this.editableText = response.generatedText?.trim()
          || response.finalText?.trim()
          || '';
        this.isSubmitting = false;
      },
      error: (error: unknown) => {
        this.errorMessage = this.extractErrorMessage(error);
        this.isSubmitting = false;
      }
    });
  }

  protected hasError(
    controlName: 'id' | 'cadastralArea' | 'parcelNumber' | 'totalArea' | 'realizedPrice' | 'date' | 'proceedingsNumber' | 'zoningCategory'
  ): boolean {
    const control = this.form.controls[controlName];
    return control.invalid && (control.dirty || control.touched);
  }

  protected previewText(): string {
    if (this.editableText.trim()) {
      return this.editableText;
    }

    if (this.isSubmitting) {
      return this.translationService.translate('comparativeObject.output.loading');
    }

    return '';
  }

  protected exportToWord(): void {
    const finalText = this.editableText.trim();

    if (!finalText || this.isExporting) {
      return;
    }

    this.isExporting = true;
    this.exportErrorMessage = '';

    this.comparativeObjectService.exportDocx({ finalText }).subscribe({
      next: (response) => {
        const fileName = this.extractFileName(response.headers.get('content-disposition'));
        const blob = response.body;

        if (!blob) {
          this.exportErrorMessage = 'Export failed because the file response was empty.';
          this.isExporting = false;
          return;
        }

        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = fileName;
        anchor.click();
        URL.revokeObjectURL(objectUrl);
        this.downloadFeedback.notifyDownloaded(fileName);
        this.isExporting = false;
      },
      error: (error: unknown) => {
        this.exportErrorMessage = this.extractErrorMessage(error, 'Unable to export Word document.');
        this.isExporting = false;
      }
    });
  }

  private toApiDate(dateValue: string): string {
    return `${dateValue}T00:00:00.000Z`;
  }

  private extractFileName(contentDisposition: string | null): string {
    const fileNameUtfMatch = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
    if (fileNameUtfMatch?.[1]) {
      return decodeURIComponent(fileNameUtfMatch[1]);
    }

    const fileNameMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
    if (fileNameMatch?.[1]) {
      return fileNameMatch[1];
    }

    return 'comparative-object.docx';
  }

  private extractErrorMessage(error: unknown, fallback = 'Unable to generate comparative object text.'): string {
    return extractHttpErrorMessage(error, fallback);
  }
}
